import { useEffect, useState } from "react";
import { MessageCircleHeart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import CircularProgress from "@/components/common/CircularProgress";
import { comunidadAdapter, MiVoz } from "@/hooks/api/comunidad";

const ANSWER_MAX = 1500;

interface VocesDeLaRedCardProps {
  editionNumber: number;
}

/**
 * "Voces de la Red" — la pregunta que cierra cada edición del newsletter. La psicóloga
 * responde ahí mismo (una respuesta por edición, que puede cambiar mientras la edición siga
 * siendo la última) y puede pedir que no aparezca su nombre si se comparte en la siguiente.
 * Solo se monta para ediciones que tienen pregunta.
 */
export const VocesDeLaRedCard = ({ editionNumber }: VocesDeLaRedCardProps) => {
  const [voz, setVoz] = useState<MiVoz | null>(null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [editing, setEditing] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    comunidadAdapter
      .getMiVoz(editionNumber)
      .then((data) => {
        if (!alive) return;
        setVoz(data);
        setAnswer(data.mine?.answer ?? "");
        setAnonymous(data.mine?.anonymous ?? false);
      })
      .catch(() => alive && setVoz(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [editionNumber]);

  const handleSubmit = async () => {
    if (!answer.trim()) return;
    setSending(true);
    try {
      // El error (p. ej. "ya no recibe respuestas") lo muestra beHttp como aviso.
      const data = await comunidadAdapter.responderVoz(editionNumber, answer.trim(), anonymous);
      setVoz(data);
      setEditing(false);
    } catch {
      /* el aviso ya se mostró */
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center rounded-2xl bg-[#F5F1FF] p-5">
        <CircularProgress />
      </div>
    );
  }
  if (!voz || !voz.question) return null;

  const showForm = voz.open && (!voz.mine || editing);

  return (
    <div id={`voces-${editionNumber}`} className="flex flex-col gap-3 rounded-2xl bg-[#F5F1FF] p-5">
      <div className="flex items-center gap-2">
        <MessageCircleHeart size={18} className="text-[#665090]" />
        <span className="text-xs font-bold uppercase tracking-wide text-[#8475B3]">Voces de la Red</span>
      </div>
      <span className="text-[15px] font-semibold leading-snug text-[#2D2049]">{voz.question}</span>

      {showForm ? (
        <>
          <span className="text-[13px] text-gray-500">
            Con lo que nos cuenten armamos una parte de la próxima edición.
          </span>
          <Textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value.slice(0, ANSWER_MAX))}
            placeholder="Escribí tu respuesta…"
            rows={4}
            className="resize-none bg-white text-sm"
            aria-label="Tu respuesta"
          />
          <label className="flex cursor-pointer items-center gap-2 text-[13px] text-[#4A3D73]">
            <input
              type="checkbox"
              checked={anonymous}
              onChange={(e) => setAnonymous(e.target.checked)}
              className="h-4 w-4 accent-[#665090]"
            />
            Prefiero que no aparezca mi nombre
          </label>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="bg-[#665090] hover:bg-[#665090]/90"
              disabled={!answer.trim() || sending}
              onClick={handleSubmit}
            >
              {sending ? "Enviando…" : voz.mine ? "Guardar cambios" : "Enviar mi respuesta"}
            </Button>
            {editing && (
              <Button
                size="sm"
                variant="ghost"
                className="text-[#665090]"
                onClick={() => {
                  setEditing(false);
                  setAnswer(voz.mine?.answer ?? "");
                  setAnonymous(voz.mine?.anonymous ?? false);
                }}
              >
                Cancelar
              </Button>
            )}
          </div>
        </>
      ) : voz.mine ? (
        <div className="flex flex-col gap-2 rounded-xl bg-white p-4">
          <span className="text-sm font-semibold text-[#2D2049]">¡Gracias por sumar tu voz! 💜</span>
          <p className="m-0 whitespace-pre-line text-sm text-[#4A3D73]">{voz.mine.answer}</p>
          {voz.mine.anonymous && (
            <span className="text-xs text-gray-500">Si la compartimos, va sin tu nombre.</span>
          )}
          {voz.open && (
            <Button
              size="sm"
              variant="outline"
              className="self-start border-[#C9BCE8] text-[#665090]"
              onClick={() => setEditing(true)}
            >
              Editar mi respuesta
            </Button>
          )}
        </div>
      ) : (
        <span className="text-[13px] text-gray-500">Esta pregunta ya cerró.</span>
      )}
    </div>
  );
};

export default VocesDeLaRedCard;
